import useSWR from "swr";
import { Box, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import {
  Building,
  Award,
  Timer1,
  Chart21,
  ShieldTick,
  ArrowRight,
} from "iconsax-react";
import baseService from "../../services/base";
import KpiCard from "./KpiCard";

// EG-38 (passo 5 BI) — landing de visão geral: KPIs de alto nível + cards que
// navegam para os painéis detalhados. É a "porta de entrada" do relatório.

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const BRAND = "#0073BB";
const GOOD = "#2F855A";
const WARN = "#C05621";
const SURFACE = "#FFFFFF";

const fetcher = (url) => baseService.get(url).then((r) => r?.data);

function NavCard({ icon: Icon, title, description, onClick }) {
  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      textAlign="left"
      bg={SURFACE}
      borderRadius="12px"
      p="20px"
      direction="column"
      gap="10px"
      border="1px solid #EDF2F7"
      transition="0.15s"
      _hover={{ borderColor: BRAND, transform: "translateY(-2px)" }}
    >
      <Flex w="40px" h="40px" borderRadius="10px" bg="#E7F1FA" align="center" justify="center">
        <Icon size="22" variant="Bulk" color={BRAND} />
      </Flex>
      <Heading size="sm" color={INK}>
        {title}
      </Heading>
      <Text fontSize="13px" color={INK_MUTED} flex="1">
        {description}
      </Text>
      <Flex align="center" gap="6px" color={BRAND} fontSize="13px" fontWeight={600}>
        Abrir <ArrowRight size="16" color={BRAND} />
      </Flex>
    </Flex>
  );
}

export default function OverviewLanding({ onNavigate }) {
  const { data: portfolio } = useSWR("metrics/portfolio", fetcher);
  const { data: dq } = useSWR("metrics/data-quality", fetcher);

  const rate = Math.round((portfolio?.certificates?.certificationRate ?? 0) * 100);
  const dqScore = dq?.score ?? null;

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="16px" mb="28px">
        <KpiCard
          label="Empresas"
          value={portfolio?.companies?.total ?? "—"}
          hint={`${portfolio?.companies?.active ?? 0} com questionário ativo`}
        />
        <KpiCard label="Taxa de certificação" value={`${rate}%`} color={BRAND} />
        <KpiCard
          label="A vencer (90 dias)"
          value={portfolio?.validity?.expiringSoon ?? "—"}
          hint={`${portfolio?.validity?.expired ?? 0} vencidas`}
          color={portfolio?.validity?.expiringSoon ? WARN : INK}
        />
        <KpiCard
          label="Qualidade dos dados"
          value={dqScore != null ? `${dqScore}%` : "—"}
          color={dqScore != null && dqScore < 60 ? WARN : GOOD}
          hint="confiabilidade da base"
        />
      </SimpleGrid>

      <Heading size="sm" color={INK} mb="12px">
        Explore os painéis
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="16px">
        <NavCard
          icon={Award}
          title="Portfólio"
          description="Nº de empresas, taxa de certificação, validade e distribuição por etapa."
          onClick={() => onNavigate("/dashboards/portfolio")}
        />
        <NavCard
          icon={Timer1}
          title="Fluxo de certificação"
          description="Tempo por etapa, lead time, gargalo e retrabalho do fluxo de certificação."
          onClick={() => onNavigate("/dashboards/fluxo")}
        />
        <NavCard
          icon={Chart21}
          title="Histórico"
          description="Evolução da nota média do portfólio ano a ano."
          onClick={() => onNavigate("/dashboards/historico")}
        />
        <NavCard
          icon={Building}
          title="Por empresa"
          description="Resultado e maturidade ESG de uma empresa específica."
          onClick={() => onNavigate("/dashboards/por-empresa")}
        />
        <NavCard
          icon={ShieldTick}
          title="Qualidade dos dados"
          description="O quanto a base que alimenta os painéis é confiável, regra a regra."
          onClick={() => onNavigate("/dashboards/qualidade")}
        />
      </SimpleGrid>
    </Box>
  );
}
